import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
// Leaf directo (no sales/public-api): el barrel carga componentes que vuelven
// a customers/cash → require cycle. Allowlist en check-module-boundaries.mjs.
import { invalidateSalesListCachesForSale } from '../../sales/infra/salesListInvalidation';
import { pullCustomerDataFromSupabaseApplication } from '../../sync/core/pullCustomersUseCase';
import {
  mapCheckCommandError,
  type CheckCommandOperation,
  type CheckRow,
  type CheckSourceType,
  type CheckStatus,
} from './checksDomain';

export type {
  CheckStatus,
  CheckSourceType,
  CheckRow,
  CheckCommandOperation,
  CheckDueBadge,
} from './checksDomain';
export {
  mapCheckCommandError,
  CHECK_STATUS_LABELS,
  CHECK_HISTORY_STATUSES,
  checkDueBadge,
  checkDueBadgeLabel,
} from './checksDomain';

const CHECK_SELECT =
  'id, number, bank, drawer, issue_date, due_date, amount, status, source_type, source_id, ' +
  'customer_id, branch_id, expense_settlement_id, rejection_reason, created_at, ' +
  'deposited_at, endorsed_at, rejected_at, returned_at, voided_at';

function mapCheckRow(row: Record<string, unknown>): CheckRow {
  const strOrNull = (v: unknown) => (v != null && String(v) !== '' ? String(v) : null);
  return {
    id: String(row.id),
    number: String(row.number ?? ''),
    bank: strOrNull(row.bank),
    drawer: strOrNull(row.drawer),
    issueDate: strOrNull(row.issue_date),
    dueDate: strOrNull(row.due_date),
    amount: Number(row.amount ?? 0),
    status: (String(row.status ?? 'in_portfolio') as CheckStatus),
    sourceType: (String(row.source_type ?? 'sale_payment') as CheckSourceType),
    sourceId: String(row.source_id ?? ''),
    customerId: strOrNull(row.customer_id),
    branchId: strOrNull(row.branch_id),
    expenseSettlementId: strOrNull(row.expense_settlement_id),
    rejectionReason: strOrNull(row.rejection_reason),
    createdAt: String(row.created_at ?? ''),
    depositedAt: strOrNull(row.deposited_at),
    endorsedAt: strOrNull(row.endorsed_at),
    rejectedAt: strOrNull(row.rejected_at),
    returnedAt: strOrNull(row.returned_at),
    voidedAt: strOrNull(row.voided_at),
  };
}

/** Cartera: cheques de la sucursal por estado, ordenados por vencimiento. */
export async function listChecksForBranch(params: {
  branchId: string;
  statuses: CheckStatus[];
  limit?: number;
}): Promise<CheckRow[]> {
  const { branchId, statuses, limit = 200 } = params;
  if (!branchId || statuses.length === 0) return [];

  const { data, error } = await supabase
    .schema('pos')
    .from('checks')
    .select(CHECK_SELECT)
    .eq('branch_id', branchId)
    .in('status', statuses)
    .order('due_date', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    captureOpsFailure({
      module: 'cash',
      op: 'checks.list',
      error: new Error(error.message),
      path: 'select',
      itemId: branchId,
      extra: { statuses: statuses.join(',') },
    });
    return [];
  }
  return (data ?? []).map((row) => mapCheckRow(row as unknown as Record<string, unknown>));
}

/** Ficha del cliente: sus cheques (todos los estados), más recientes primero. */
export async function listChecksForCustomer(params: {
  customerId: string;
  limit?: number;
}): Promise<CheckRow[]> {
  const { customerId, limit = 50 } = params;
  if (!customerId) return [];

  const { data, error } = await supabase
    .schema('pos')
    .from('checks')
    .select(CHECK_SELECT)
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    captureOpsFailure({
      module: 'cash',
      op: 'checks.list_by_customer',
      error: new Error(error.message),
      path: 'select',
      itemId: customerId,
    });
    return [];
  }
  return (data ?? []).map((row) => mapCheckRow(row as unknown as Record<string, unknown>));
}

/**
 * Lookup de "tengo el papel en la mano": devuelve los customer_id cuyos cheques
 * matchean el texto (número, banco o librador). La lista de Clientes los usa
 * como extra-matches del search — el dueño encuentra al cliente por el cheque.
 */
export async function findCustomerIdsByCheckText(text: string): Promise<string[]> {
  const q = text.trim().replace(/[%_,]/g, ' ');
  if (q.length < 2) return [];

  const pattern = `%${q}%`;
  const { data, error } = await supabase
    .schema('pos')
    .from('checks')
    .select('customer_id')
    .or(`number.ilike.${pattern},bank.ilike.${pattern},drawer.ilike.${pattern}`)
    .limit(100);

  if (error) {
    captureOpsFailure({
      module: 'cash',
      op: 'checks.lookup_customers',
      error: new Error(error.message),
      path: 'select',
    });
    return [];
  }
  return [...new Set(
    (data ?? [])
      .map((row) => String((row as { customer_id?: unknown }).customer_id ?? ''))
      .filter(Boolean)
  )];
}

export async function getCheckById(checkId: string): Promise<CheckRow | null> {
  const { data, error } = await supabase
    .schema('pos')
    .from('checks')
    .select(CHECK_SELECT)
    .eq('id', checkId)
    .maybeSingle();
  if (error || !data) return null;
  return mapCheckRow(data as unknown as Record<string, unknown>);
}

/** Transiciones server-authoritative. Idempotente vía operationId. */
export async function applyCheckCommand(params: {
  operation: CheckCommandOperation;
  checkId: string;
  settlementId?: string;
  expenseId?: string;
  reason?: string;
  note?: string;
  operationId: string;
}): Promise<void> {
  const { data, error } = await supabase.schema('pos').rpc('apply_check_command', {
    p_operation: params.operation,
    p_payload: {
      checkId: params.checkId,
      settlementId: params.settlementId ?? null,
      expenseId: params.expenseId ?? null,
      reason: params.reason ?? null,
      note: params.note ?? null,
      operationId: params.operationId,
    },
  });
  if (error) throw new Error(mapCheckCommandError(error.message));
  refreshAfterCheckCommand(data);
}

/**
 * Rechazo/devolución revierten el pago server-side (ventas vuelven a pendiente
 * y se re-carga la deuda). Invalida caches de ventas y dispara el pull de
 * clientes — el snapshot RAM se actualiza y las pantallas suscriptas
 * (lista, KPIs, ficha abierta) refrescan solas. Best-effort, fire-and-forget.
 */
function refreshAfterCheckCommand(result: unknown): void {
  const payload = (result ?? {}) as {
    branch_id?: unknown;
    affected_sale_ids?: unknown;
    customer_recharged?: unknown;
    customer_balance_changed?: unknown;
  };
  const saleIds = Array.isArray(payload.affected_sale_ids)
    ? payload.affected_sale_ids.map((id) => String(id)).filter(Boolean)
    : [];
  const branchId = typeof payload.branch_id === 'string' ? payload.branch_id : null;
  const balanceChanged =
    payload.customer_balance_changed === true || payload.customer_recharged === true;
  if (saleIds.length === 0 && !balanceChanged) return;

  void (async () => {
    try {
      if (branchId) {
        for (const saleId of saleIds) {
          invalidateSalesListCachesForSale(branchId, saleId);
        }
      }
      await pullCustomerDataFromSupabaseApplication();
    } catch (err) {
      captureOpsFailure({
        module: 'cash',
        op: 'checks.post_refresh',
        error: err instanceof Error ? err : new Error(String(err)),
      });
    }
  })();
}
